/**
 * Geometry shell sub-reducer — handles all geometry/* commands on the
 * shell-state side (audit events, entity preview creation, selection).
 * Extracted from editorStore.ts — pure structural refactor.
 *
 * Returns the next EditorShellState if the command matched,
 * or null if the command type is not handled here.
 */

import {
  appendCreatedEntity,
  createArcEntity,
  createCircleEntity,
  createEllipseEntity,
  createLineEntity,
  createPointEntity,
  createViewportEntity,
  guardScale,
  heavyPolylineCurveRefusalFor,
  mirrorPoint,
  resolveRevcloudObjectSource,
  rotatePoint,
  summarizeExplodeInsertRefusal,
  summarizeHeavyPolylineExplodeRefusal,
  summarizeHeavyPolylineTrimRefusal,
  summarizeOleFrameTransformRefusal,
  summarizeTableMirrorRefusal,
  summarizeTableScaleRefusal,
  type ExplodeInsertRefusalSummary,
  type HeavyPolylineExplodeRefusalSummary,
} from "@dxf-ai-editor/command-engine";
import type {
  DynamicBlockCommand,
  EditorCommand,
  EditorShellState,
  EntityPreview,
} from "@dxf-ai-editor/contracts";
import {
  colorToLegacyString,
  type DrawingDocument,
  type PolylineEntity,
} from "@dxf-ai-editor/drawing-model";

import { t } from "../i18n";
// Whether a `geometry/create-arraypath` dispatch earns its success row — the
// verdict is the document reducer's own RESULT, not a list of refusal reasons
// re-derived here. Its own leaf; see that module's header.
import { arrayPathDeclinedAudit } from "./arrayPathShellAudit";
import { nextEntityId, pushAuditEvent } from "./reducerHelpers";
import { isPaperSpaceLayoutTarget } from "./geometryReducerHelpers";
import { reduceViewportEditShellAudit } from "./geometryShellViewportAudit";
// The heavy-POLYLINE refusal AUDIT LINES (TRIM / EXTEND, OFFSET, ARRAYPATH) —
// their own leaf since @codex PR #785 round 14, when the OFFSET and ARRAYPATH
// rows joined TRIM's and pushed this file past its 2500-line cap. Pure verdict
// → localised string; the VERDICT itself is still `command-engine`'s one truth.
import {
  heavyPolylineExplodeRefusalAudit,
  heavyPolylineRefusalAudit,
  offsetHeavyPolylineRefusalAudit,
} from "./geometryShellHeavyPolylineAudit";

/**
 * The user-facing line for an EXPLODE dispatch the document reducer DECLINED,
 * or `null` when it declined nothing.
 *
 * EXPLODE is all-or-nothing: a block definition holding a child whose enclosing
 * INSERT affine its own fields cannot absorb (an `ACAD_TABLE` under anything but
 * a proper rotation, an `OLE2FRAME` under an oblique one) leaves the INSERT
 * entirely alone. Before the refusal existed, `explodeChild`'s `default:` arm
 * dropped those children while `explodeBlockInsertInDocument` removed the parent
 * anyway — silent data loss on exactly the fixture the kinds were admitted for.
 *
 * ONE validity truth with the document reducer: both call
 * `command-engine/explodeBlockChildPolylineTableOle.ts`'s
 * {@link summarizeExplodeInsertRefusal} on the SAME guarded scales, so the shell
 * can never report a refusal the document did not make, nor stay silent about
 * one it did. A carryable INSERT refuses nothing and keeps the plain success
 * row, byte-for-byte as before.
 *
 * A declined TABLE wins over a declined OLE FRAME when a definition holds both —
 * same precedence shape as {@link heavyPolylineRefusalAudit}: the command did
 * nothing at all either way, and one reason is enough to say so.
 */
function explodeRefusalAudit(
  refusal: ExplodeInsertRefusalSummary,
): string | null {
  if (refusal.refusedTableCount > 0) {
    return t("audit.explode_declined_table", { n: refusal.refusedTableCount });
  }
  if (refusal.refusedOleFrameCount > 0) {
    return t("audit.explode_declined_ole_frame", {
      n: refusal.refusedOleFrameCount,
    });
  }
  return null;
}

/**
 * Union the EXPLODE refusal over every INSERT the dispatch targets, resolving
 * each id against the document exactly as the reducer does — so the shell can
 * only speak about INSERTs the reducer actually reaches.
 *
 * Declining nothing, because the reducer no-ops on them for its OWN reasons and
 * a refusal row would then be a lie about WHY nothing happened: an id matching
 * no entity, an id whose entity is not an INSERT, an INSERT whose block
 * definition is missing, and an INSERT of a block whose `BlockRecord` is marked
 * NON-EXPLODABLE (`explodable === false` — the same guard
 * `geometry/explode-insert` and `geometry/explode-entities`' Block branch
 * apply before they call `explodeBlockInsertInDocument` at all).
 */
function summarizeExplodeRefusalForIds(
  geometryDocument: DrawingDocument,
  entityIds: readonly string[],
): ExplodeInsertRefusalSummary {
  const wanted = new Set(entityIds);
  let refusedTableCount = 0;
  let refusedOleFrameCount = 0;
  for (const space of geometryDocument.spaces) {
    for (const entity of space.entities) {
      if (!wanted.has(entity.id)) continue;
      if (entity.type !== "Block") continue;
      const blockDef = geometryDocument.blocks[entity.blockName];
      if (!blockDef) continue;
      if (
        geometryDocument.blockRecords[entity.blockName]?.explodable === false
      ) {
        continue;
      }
      const refusal = summarizeExplodeInsertRefusal(blockDef.entities, {
        rotationDeg: entity.rotation,
        scaleX: guardScale(entity.scaleX),
        scaleY: guardScale(entity.scaleY),
      });
      refusedTableCount += refusal.refusedTableCount;
      refusedOleFrameCount += refusal.refusedOleFrameCount;
    }
  }
  return {
    refusedTableCount,
    refusedOleFrameCount,
    refused: refusedTableCount > 0 || refusedOleFrameCount > 0,
  };
}

/**
 * Resolve the dispatch's ids against the document and summarise the heavy
 * POLYLINE refusal over exactly the ones the reducer's `"Polyline"` arm
 * reaches — an id matching no entity, or matching an entity of another kind,
 * contributes nothing for the same reason it does in
 * {@link summarizeExplodeRefusalForIds}.
 */
function summarizeHeavyPolylineExplodeRefusalForIds(
  geometryDocument: DrawingDocument,
  entityIds: readonly string[],
): HeavyPolylineExplodeRefusalSummary {
  const wanted = new Set(entityIds);
  const polylines: PolylineEntity[] = [];
  for (const space of geometryDocument.spaces) {
    for (const entity of space.entities) {
      if (!wanted.has(entity.id)) continue;
      if (entity.type !== "Polyline") continue;
      polylines.push(entity);
    }
  }
  return summarizeHeavyPolylineExplodeRefusal(polylines);
}

export function reduceGeometryShellCommand(
  state: EditorShellState,
  command: EditorCommand | DynamicBlockCommand,
  geometryDocument: DrawingDocument,
): EditorShellState | null {
  switch (command.type) {
    case "geometry/create-line": {
      const created = createLineEntity({
        nextId: nextEntityId(state, "line", geometryDocument),
        spaceId: command.spaceId,
        start: command.start,
        end: command.end,
        // CECOLOR — the preview Color bag must match the typed-document entity
        // (the shell reducer's `created.entity` Color = the doc entity's colour).
        ...(command.color !== undefined ? { color: command.color } : {}),
      });

      return pushAuditEvent(
        {
          ...state,
          ui: {
            activePanel: "properties",
          },
          drawing: {
            ...state.drawing,
            entities: appendCreatedEntity(
              state.drawing.entities,
              created.entity,
            ),
          },
          selection: {
            entityIds: [created.entity.id],
          },
        },
        command,
        created.summary,
        "success",
        undefined,
        // CC-1b R5 (@codex) — the created id is allocated here, not on the
        // command, so hand it to the audit event as the affected-entity evidence.
        [created.entity.id],
      );
    }

    case "geometry/create-point": {
      const created = createPointEntity({
        nextId: nextEntityId(state, "point", geometryDocument),
        spaceId: command.spaceId,
        location: command.location,
        rotation: command.rotation,
        ...(command.color !== undefined ? { color: command.color } : {}),
      });

      return pushAuditEvent(
        {
          ...state,
          ui: {
            activePanel: "properties",
          },
          drawing: {
            ...state.drawing,
            entities: appendCreatedEntity(
              state.drawing.entities,
              created.entity,
            ),
          },
          selection: {
            entityIds: [created.entity.id],
          },
        },
        command,
        created.summary,
        "success",
        undefined,
        // CC-1b R5 (@codex) — the created id is allocated here, not on the
        // command, so hand it to the audit event as the affected-entity evidence.
        [created.entity.id],
      );
    }

    case "geometry/create-circle": {
      const created = createCircleEntity({
        nextId: nextEntityId(state, "circle", geometryDocument),
        spaceId: command.spaceId,
        center: command.center,
        radius: command.radius,
        ...(command.color !== undefined ? { color: command.color } : {}),
      });

      return pushAuditEvent(
        {
          ...state,
          ui: {
            activePanel: "properties",
          },
          drawing: {
            ...state.drawing,
            entities: appendCreatedEntity(
              state.drawing.entities,
              created.entity,
            ),
          },
          selection: {
            entityIds: [created.entity.id],
          },
        },
        command,
        created.summary,
        "success",
        undefined,
        // CC-1b R5 (@codex) — the created id is allocated here, not on the
        // command, so hand it to the audit event as the affected-entity evidence.
        [created.entity.id],
      );
    }

    case "geometry/create-arc": {
      const created = createArcEntity({
        nextId: nextEntityId(state, "arc", geometryDocument),
        spaceId: command.spaceId,
        center: command.center,
        radius: command.radius,
        startAngle: command.startAngle,
        endAngle: command.endAngle,
        ...(command.color !== undefined ? { color: command.color } : {}),
      });

      return pushAuditEvent(
        {
          ...state,
          ui: {
            activePanel: "properties",
          },
          drawing: {
            ...state.drawing,
            entities: appendCreatedEntity(
              state.drawing.entities,
              created.entity,
            ),
          },
          selection: {
            entityIds: [created.entity.id],
          },
        },
        command,
        created.summary,
        "success",
        undefined,
        // CC-1b R5 (@codex) — the created id is allocated here, not on the
        // command, so hand it to the audit event as the affected-entity evidence.
        [created.entity.id],
      );
    }

    case "geometry/create-ellipse": {
      // Mirror the geometryReducer ratio/swap semantics so the preview
      // entity carries the same major / minor canonical orientation as
      // the persisted DrawingDocument entity. AutoCAD parity: major-axis
      // is always the longer of the two; ratio in (0, 1].
      const majorVx = command.majorAxisEnd.x - command.center.x;
      const majorVy = command.majorAxisEnd.y - command.center.y;
      const majorLen = Math.hypot(majorVx, majorVy);
      const minorLen = Math.max(command.minorDistance, 1e-9);
      let majorAxisEndpoint = { x: majorVx, y: majorVy };
      let ratio: number;
      if (majorLen <= 1e-9) {
        majorAxisEndpoint = { x: 1, y: 0 };
        ratio = Math.min(1, minorLen);
      } else if (minorLen > majorLen) {
        majorAxisEndpoint = {
          x: -majorVy * (minorLen / majorLen),
          y: majorVx * (minorLen / majorLen),
        };
        ratio = majorLen / minorLen;
      } else {
        ratio = minorLen / majorLen;
      }

      const created = createEllipseEntity({
        nextId: nextEntityId(state, "ellipse", geometryDocument),
        spaceId: command.spaceId,
        center: { x: command.center.x, y: command.center.y },
        majorAxisEndpoint,
        ratio,
        startParam: command.startParam,
        endParam: command.endParam,
        ...(command.color !== undefined ? { color: command.color } : {}),
      });

      return pushAuditEvent(
        {
          ...state,
          ui: {
            activePanel: "properties",
          },
          drawing: {
            ...state.drawing,
            entities: appendCreatedEntity(
              state.drawing.entities,
              created.entity,
            ),
          },
          selection: {
            entityIds: [created.entity.id],
          },
        },
        command,
        created.summary,
        "success",
        undefined,
        // CC-1b R5 (@codex) — the created id is allocated here, not on the
        // command, so hand it to the audit event as the affected-entity evidence.
        [created.entity.id],
      );
    }

    case "geometry/create-lwpolyline": {
      const isRect =
        command.closed &&
        command.vertices.length === 4 &&
        command.vertices.every((v) => (v.bulge ?? 0) === 0);
      const polyId = nextEntityId(state, isRect ? "rect" : "pline", geometryDocument);
      const vertexCount = command.vertices.length;
      const polyEntity: EntityPreview = {
        id: polyId,
        label: `${isRect ? "Rectangle" : "Polyline"} ${polyId}`,
        type: "LWPOLYLINE",
        spaceId: command.spaceId,
        summary: `LWPOLYLINE created with ${vertexCount} vertices${command.closed ? " (closed)" : ""}.`,
        editableProperties: [
          "Layer",
          "Color",
          "Linetype",
          "Lineweight",
          "Constant width",
          "Closed",
          "Vertices",
        ],
        properties: {
          Layer: "0",
          // CECOLOR — project the stamped current colour to the legacy Color
          // string so the preview matches the typed-document LWPOLYLINE entity;
          // ByLayer default when the command omits a colour.
          Color: colorToLegacyString(command.color ?? { kind: "byLayer" }),
          Linetype: "ByLayer",
          Lineweight: "ByLayer",
          "Constant width": "0",
          // Canonical DXF/adapter form — see comment in
          // `dxfGeometry.ts:buildLwpolylinePreview`.
          Closed: command.closed ? "true" : "false",
          Vertices: command.vertices.map((v) => `${v.x},${v.y}`).join(";"),
        },
      };

      return pushAuditEvent(
        {
          ...state,
          ui: { activePanel: "properties" },
          drawing: {
            ...state.drawing,
            entities: appendCreatedEntity(state.drawing.entities, polyEntity),
          },
          selection: { entityIds: [polyEntity.id] },
        },
        command,
        polyEntity.summary,
        "success",
        undefined,
        [polyEntity.id],
      );
    }

    case "geometry/create-text": {
      const textId = nextEntityId(state, "text", geometryDocument);
      const textEntity: EntityPreview = {
        id: textId,
        label: `Text ${textId}`,
        type: "Text",
        spaceId: command.spaceId,
        summary: `TEXT created at ${command.insertionPoint.x},${command.insertionPoint.y}.`,
        editableProperties: [
          "Layer",
          "Color",
          "Contents",
          "Text style",
          "Height",
          "Rotation",
          "Justify",
          "Insertion X",
          "Insertion Y",
          "Width factor",
          "Oblique angle",
          "Mirror X",
          "Mirror Y",
        ],
        properties: {
          Layer: "0",
          Color: "ByLayer",
          Contents: command.contents,
          "Text style": command.styleName,
          Height: String(command.height),
          Rotation: String(command.rotation),
          Justify: "Left",
          "Insertion X": String(command.insertionPoint.x),
          "Insertion Y": String(command.insertionPoint.y),
          "Width factor": "1",
          "Oblique angle": "0",
          "Mirror X": "No",
          "Mirror Y": "No",
        },
      };

      return pushAuditEvent(
        {
          ...state,
          ui: { activePanel: "properties" },
          drawing: {
            ...state.drawing,
            entities: appendCreatedEntity(state.drawing.entities, textEntity),
          },
          selection: { entityIds: [textEntity.id] },
        },
        command,
        textEntity.summary,
        "success",
        undefined,
        [textEntity.id],
      );
    }

    case "geometry/create-mtext": {
      const mtextId = nextEntityId(state, "mtext", geometryDocument);
      const mtextEntity: EntityPreview = {
        id: mtextId,
        label: `MText ${mtextId}`,
        type: "MText",
        spaceId: command.spaceId,
        summary: `MTEXT created at ${command.insertionPoint.x},${command.insertionPoint.y}.`,
        editableProperties: [
          "Layer",
          "Color",
          "Contents",
          "Text style",
          "Height",
          "Width",
          "Attachment",
          "Rotation",
          "Line spacing",
          "Background fill",
          "Insertion X",
          "Insertion Y",
        ],
        properties: {
          Layer: "0",
          Color: "ByLayer",
          Contents: command.contents,
          "Text style": command.styleName,
          Height: String(command.nominalHeight),
          Width: String(command.referenceWidth),
          Attachment: "Top left",
          Rotation: "0",
          "Line spacing": "1",
          "Background fill": "Off",
          "Insertion X": String(command.insertionPoint.x),
          "Insertion Y": String(command.insertionPoint.y),
        },
      };

      return pushAuditEvent(
        {
          ...state,
          ui: { activePanel: "properties" },
          drawing: {
            ...state.drawing,
            entities: appendCreatedEntity(state.drawing.entities, mtextEntity),
          },
          selection: { entityIds: [mtextEntity.id] },
        },
        command,
        mtextEntity.summary,
        "success",
        undefined,
        [mtextEntity.id],
      );
    }

    case "geometry/create-dimension": {
      const dimId = nextEntityId(state, "dimension", geometryDocument);
      const dimType = command.dimensionType;
      const ext1 = command.extLine1Point;
      const ext2 = command.extLine2Point;
      const dimLine = command.dimensionLinePoint;
      const thirdPt = command.thirdPoint;

      const dimSubtypeNames: Record<number, string> = {
        0: "Linear",
        1: "Aligned",
        2: "Angular",
        3: "Diameter",
        4: "Radius",
        5: "Angular 3pt",
        6: "Ordinate",
      };
      const subtypeName = dimSubtypeNames[dimType] ?? "Linear";

      let dimMeasurement: number;
      if (dimType === 1) {
        dimMeasurement = Math.hypot(ext2.x - ext1.x, ext2.y - ext1.y);
      } else if (dimType === 2) {
        // Angular 2-line: vertex inferred from dimensionLinePoint.
        const v1x = ext1.x - dimLine.x;
        const v1y = ext1.y - dimLine.y;
        const v2x = ext2.x - dimLine.x;
        const v2y = ext2.y - dimLine.y;
        const dot = v1x * v2x + v1y * v2y;
        const m1 = Math.hypot(v1x, v1y) || 1;
        const m2 = Math.hypot(v2x, v2y) || 1;
        const cos = Math.max(-1, Math.min(1, dot / (m1 * m2)));
        dimMeasurement = (Math.acos(cos) * 180) / Math.PI;
      } else if (dimType === 5) {
        // Angular 3-point: vertex is explicit (thirdPoint).
        const vx = thirdPt?.x ?? dimLine.x;
        const vy = thirdPt?.y ?? dimLine.y;
        const v1x = ext1.x - vx;
        const v1y = ext1.y - vy;
        const v2x = ext2.x - vx;
        const v2y = ext2.y - vy;
        const dot = v1x * v2x + v1y * v2y;
        const m1 = Math.hypot(v1x, v1y) || 1;
        const m2 = Math.hypot(v2x, v2y) || 1;
        const cos = Math.max(-1, Math.min(1, dot / (m1 * m2)));
        dimMeasurement = (Math.acos(cos) * 180) / Math.PI;
      } else if (dimType === 3) {
        dimMeasurement = Math.hypot(ext2.x - ext1.x, ext2.y - ext1.y);
      } else if (dimType === 4) {
        dimMeasurement = Math.hypot(dimLine.x - ext1.x, dimLine.y - ext1.y);
      } else if (dimType === 6) {
        const dx = Math.abs(ext2.x - ext1.x);
        const dy = Math.abs(ext2.y - ext1.y);
        dimMeasurement = dx >= dy ? Math.abs(ext1.x) : Math.abs(ext1.y);
      } else {
        dimMeasurement = Math.hypot(ext2.x - ext1.x, ext2.y - ext1.y);
      }

      const dimEntity: EntityPreview = {
        id: dimId,
        label: `${subtypeName} Dimension ${dimId}`,
        type: "Dimension",
        spaceId: command.spaceId,
        summary: `${subtypeName} DIMENSION created, measurement ${dimMeasurement.toFixed(3)}.`,
        editableProperties: [
          "Layer",
          "Color",
          "Dimension style",
          "Measurement",
          "Text override",
          "Subtype",
          "Extension 1 X",
          "Extension 1 Y",
          "Extension 2 X",
          "Extension 2 Y",
          "Dimension line X",
          "Dimension line Y",
          "Angle",
          "Text height",
          "Text rotation",
          "Third point X",
          "Third point Y",
        ],
        properties: {
          Layer: "0",
          Color: "ByLayer",
          "Dimension style": "Standard",
          Measurement: String(Number(dimMeasurement.toFixed(3))),
          "Text override": command.dimensionText || "<>",
          Subtype: subtypeName,
          "Extension 1 X": String(ext1.x),
          "Extension 1 Y": String(ext1.y),
          "Extension 2 X": String(ext2.x),
          "Extension 2 Y": String(ext2.y),
          "Dimension line X": String(dimLine.x),
          "Dimension line Y": String(dimLine.y),
          // For 3-point angular, surface the vertex coordinates so the
          // renderer's Third point X/Y reads pick them up.
          ...(thirdPt
            ? {
                "Third point X": String(thirdPt.x),
                "Third point Y": String(thirdPt.y),
              }
            : {}),
          Angle: "0",
          // "Text height" = 0 means "use the dimension style's DIMTXT". This
          // mirrors AutoCAD's behaviour and lets per-style DIMTXT changes
          // flow through to the renderer.
          "Text height": "0",
          "Text rotation": "0",
        },
      };

      return pushAuditEvent(
        {
          ...state,
          ui: { activePanel: "properties" },
          drawing: {
            ...state.drawing,
            entities: appendCreatedEntity(state.drawing.entities, dimEntity),
          },
          selection: { entityIds: [dimEntity.id] },
        },
        command,
        dimEntity.summary,
        "success",
        undefined,
        [dimEntity.id],
      );
    }

    case "geometry/create-multileader": {
      const mleaderId = nextEntityId(state, "mleader", geometryDocument);
      // Phase 9 — the grip-edit renderer reads the leader hierarchy
      // from `__mleader_leader_roots`. The shell-side handler builds a
      // flat preview that did not include this key before, so the new
      // mleader grips would not render until the next dispatch that
      // triggers `syncShellStateFromGeometryDocument`. Seed the
      // hierarchy here directly so grips are available immediately
      // after creation. The shape mirrors `encodeLeaderRoots` in
      // dxfGeometry.ts — one root / one node / one line with a single
      // arrow vertex.
      const initialLeaderRootsJson = JSON.stringify([
        {
          n: [
            {
              d: null,
              l: [
                {
                  v: [[command.arrowPoint.x, command.arrowPoint.y]],
                  b: [],
                  a: "",
                },
              ],
            },
          ],
        },
      ]);
      const mleaderEntity: EntityPreview = {
        id: mleaderId,
        label: `MLeader ${mleaderId}`,
        type: "Multileader",
        spaceId: command.spaceId,
        summary: `MLEADER created from ${command.arrowPoint.x},${command.arrowPoint.y} to ${command.landingPoint.x},${command.landingPoint.y}.`,
        editableProperties: [
          "Layer",
          "Color",
          "Multileader style",
          "Contents",
          "Arrow X",
          "Arrow Y",
          "Landing X",
          "Landing Y",
          "Arrowhead size",
          "Text height",
          "Attachment",
          "Landing gap",
          "Leader color",
          "Leader weight",
          "Leader type",
        ],
        properties: {
          Layer: "0",
          Color: "ByLayer",
          "Multileader style": "Standard",
          Contents: command.textContents,
          "Arrow X": String(command.arrowPoint.x),
          "Arrow Y": String(command.arrowPoint.y),
          "Landing X": String(command.landingPoint.x),
          "Landing Y": String(command.landingPoint.y),
          "Arrowhead size": "2.5",
          "Text height": "2.5",
          Attachment: "Top left",
          "Landing gap": "2",
          "Leader color": "ByBlock",
          "Leader weight": "ByBlock",
          "Leader type": "1",
          // Phase 9 — grip rendering hints.
          MLENT_LANDING_ENABLED: "true",
          MLENT_DOGLEG_ENABLED: "true",
          MLENT_DOGLEG_LENGTH: "8",
          __mleader_leader_roots: initialLeaderRootsJson,
        },
      };

      return pushAuditEvent(
        {
          ...state,
          ui: { activePanel: "properties" },
          drawing: {
            ...state.drawing,
            entities: appendCreatedEntity(
              state.drawing.entities,
              mleaderEntity,
            ),
          },
          selection: { entityIds: [mleaderEntity.id] },
        },
        command,
        mleaderEntity.summary,
        "success",
        undefined,
        [mleaderEntity.id],
      );
    }

    case "geometry/add-leader-to-multileader": {
      // Shell-state side of MLEADEREDIT Add Leader. The geometry-doc
      // reducer owns the leaderRoots mutation; here we just emit an
      // audit event so the user sees feedback. The shell preview list
      // is rebuilt from the geometry document by the dispatch loop
      // (via `syncShellStateFromGeometryDocument` — see the
      // `needsShellResyncFromGeometry` branch in `dispatch`).
      const targetEntity = state.drawing.entities.find(
        (entity) => entity.id === command.entityId,
      );
      if (!targetEntity) {
        return pushAuditEvent(
          state,
          command,
          t("summary.mleader_not_found"),
          "warning",
        );
      }
      if (targetEntity.type !== "Multileader") {
        return pushAuditEvent(
          state,
          command,
          t("summary.mleader_not_multileader"),
          "warning",
        );
      }
      return pushAuditEvent(
        {
          ...state,
          ui: { activePanel: "properties" },
          selection: { entityIds: [command.entityId] },
        },
        command,
        t("summary.mleader_leader_added"),
        "success",
      );
    }

    case "geometry/remove-leader-from-multileader": {
      // Shell-state side of MLEADEREDIT Remove Leader.
      const targetEntity = state.drawing.entities.find(
        (entity) => entity.id === command.entityId,
      );
      if (!targetEntity) {
        return pushAuditEvent(
          state,
          command,
          t("summary.mleader_not_found"),
          "warning",
        );
      }
      if (targetEntity.type !== "Multileader") {
        return pushAuditEvent(
          state,
          command,
          t("summary.mleader_not_multileader"),
          "warning",
        );
      }
      // The geometry reducer will either:
      //  (a) remove the targeted leader line (if valid index),
      //  (b) leave state unchanged (if last leader and !confirmed), or
      //  (c) delete the whole entity (if last leader and confirmed).
      // We emit an informational audit event keyed on the `confirmed`
      // flag — the UI layer is responsible for the confirm dialog.
      const summaryKey = command.confirmed
        ? "summary.mleader_removed"
        : "summary.mleader_leader_removed";
      return pushAuditEvent(
        {
          ...state,
          ui: { activePanel: "properties" },
          selection: command.confirmed
            ? { entityIds: [] }
            : { entityIds: [command.entityId] },
        },
        command,
        t(summaryKey),
        "success",
      );
    }

    // ── Phase 9 — Grip-edit shell-side audit events ─────────────
    // The geometry-document reducer owns the actual mutation; this
    // pass only emits an audit event so the user gets feedback in the
    // command-line transcript. Validation (entity exists, is a
    // multileader) is mirrored here so a misuse from the UI layer
    // surfaces as a warning audit event rather than a silent no-op.
    case "geometry/update-multileader-vertex": {
      const targetEntity = state.drawing.entities.find(
        (entity) => entity.id === command.entityId,
      );
      if (!targetEntity) {
        return pushAuditEvent(
          state,
          command,
          t("summary.mleader_not_found"),
          "warning",
        );
      }
      if (targetEntity.type !== "Multileader") {
        return pushAuditEvent(
          state,
          command,
          t("summary.mleader_not_multileader"),
          "warning",
        );
      }
      return pushAuditEvent(
        {
          ...state,
          ui: { activePanel: "properties" },
          selection: { entityIds: [command.entityId] },
        },
        command,
        t("summary.mleader_vertex_moved"),
        "success",
      );
    }

    case "geometry/update-multileader-text-location": {
      const targetEntity = state.drawing.entities.find(
        (entity) => entity.id === command.entityId,
      );
      if (!targetEntity) {
        return pushAuditEvent(
          state,
          command,
          t("summary.mleader_not_found"),
          "warning",
        );
      }
      if (targetEntity.type !== "Multileader") {
        return pushAuditEvent(
          state,
          command,
          t("summary.mleader_not_multileader"),
          "warning",
        );
      }
      return pushAuditEvent(
        {
          ...state,
          ui: { activePanel: "properties" },
          selection: { entityIds: [command.entityId] },
        },
        command,
        t("summary.mleader_text_moved"),
        "success",
      );
    }

    case "geometry/update-multileader-text-rotation": {
      const targetEntity = state.drawing.entities.find(
        (entity) => entity.id === command.entityId,
      );
      if (!targetEntity) {
        return pushAuditEvent(
          state,
          command,
          t("summary.mleader_not_found"),
          "warning",
        );
      }
      if (targetEntity.type !== "Multileader") {
        return pushAuditEvent(
          state,
          command,
          t("summary.mleader_not_multileader"),
          "warning",
        );
      }
      return pushAuditEvent(
        {
          ...state,
          ui: { activePanel: "properties" },
          selection: { entityIds: [command.entityId] },
        },
        command,
        t("summary.mleader_text_rotated"),
        "success",
      );
    }

    case "geometry/update-multileader-dogleg": {
      const targetEntity = state.drawing.entities.find(
        (entity) => entity.id === command.entityId,
      );
      if (!targetEntity) {
        return pushAuditEvent(
          state,
          command,
          t("summary.mleader_not_found"),
          "warning",
        );
      }
      if (targetEntity.type !== "Multileader") {
        return pushAuditEvent(
          state,
          command,
          t("summary.mleader_not_multileader"),
          "warning",
        );
      }
      return pushAuditEvent(
        {
          ...state,
          ui: { activePanel: "properties" },
          selection: { entityIds: [command.entityId] },
        },
        command,
        t("summary.mleader_dogleg_adjusted"),
        "success",
      );
    }

    case "geometry/remove-multileader-vertex": {
      const targetEntity = state.drawing.entities.find(
        (entity) => entity.id === command.entityId,
      );
      if (!targetEntity) {
        return pushAuditEvent(
          state,
          command,
          t("summary.mleader_not_found"),
          "warning",
        );
      }
      if (targetEntity.type !== "Multileader") {
        return pushAuditEvent(
          state,
          command,
          t("summary.mleader_not_multileader"),
          "warning",
        );
      }
      return pushAuditEvent(
        {
          ...state,
          ui: { activePanel: "properties" },
          selection: { entityIds: [command.entityId] },
        },
        command,
        t("summary.mleader_vertex_removed"),
        "success",
      );
    }

    case "geometry/add-multileader-vertex": {
      const targetEntity = state.drawing.entities.find(
        (entity) => entity.id === command.entityId,
      );
      if (!targetEntity) {
        return pushAuditEvent(
          state,
          command,
          t("summary.mleader_not_found"),
          "warning",
        );
      }
      if (targetEntity.type !== "Multileader") {
        return pushAuditEvent(
          state,
          command,
          t("summary.mleader_not_multileader"),
          "warning",
        );
      }
      return pushAuditEvent(
        {
          ...state,
          ui: { activePanel: "properties" },
          selection: { entityIds: [command.entityId] },
        },
        command,
        t("summary.mleader_vertex_added"),
        "success",
      );
    }

    case "geometry/create-revcloud": {
      // REVCLOUD-MODES — corners are optional for Polygonal / Freehand /
      // Object payloads; the shell summary falls back to the vertex /
      // source description. The geometry document reducer owns the real
      // shape; this preview row is audit/selection chrome only.
      const rcMode = command.creationMode ?? "Rectangular";
      // ONE validity truth with the geometry document reducer (geometryReducer
      // create-revcloud arm): when that reducer would no-op the command
      // (NO_MUTATIONS), the shell must NOT append/select a preview row — the
      // resync after a no-op mutation set REUSES shell entities (it does not
      // rebuild), so an appended row becomes a phantom cloud that exists in
      // the UI but not in the document. Reachable via AI/API typed dispatch:
      // the interactive picker validates the source at input time, the typed
      // command path doesn't (codex P2 on PR #312).
      if (rcMode === "Object") {
        if (
          !resolveRevcloudObjectSource(
            geometryDocument.spaces,
            command.sourceEntityId,
            command.arcChordLength,
          )
        ) {
          // AutoCAD: "The selected object is not a valid closed object."
          return pushAuditEvent(
            state,
            command,
            t("prompt.revcloud.invalid_object"),
            "warning",
          );
        }
      } else if (
        rcMode === "Polygonal" || rcMode === "Freehand"
          ? (command.vertices?.length ?? 0) < 3
          : !command.corner1 || !command.corner2
      ) {
        // Degenerate payload the document reducer no-ops (vertices[<3] /
        // missing corners). The contract refine blocks parsed commands;
        // unparsed direct dispatches can still reach the reducers, so the
        // shell mirrors the document-reducer guards exactly.
        return pushAuditEvent(
          state,
          command,
          t("summary.revcloud_payload_invalid"),
          "warning",
        );
      }
      const rcId = nextEntityId(state, "revcloud", geometryDocument);
      const rcSummary =
        rcMode === "Object"
          ? `REVCLOUD created from object ${command.sourceEntityId ?? ""}.`
          : rcMode === "Rectangular" && command.corner1 && command.corner2
            ? `REVCLOUD created from ${command.corner1.x},${command.corner1.y} to ${command.corner2.x},${command.corner2.y}.`
            : `REVCLOUD created along ${command.vertices?.length ?? 0} vertices.`;
      const rcEntity: EntityPreview = {
        id: rcId,
        label: `Revision cloud ${rcId}`,
        type: "Revision Cloud",
        spaceId: command.spaceId,
        summary: rcSummary,
        editableProperties: [
          "Layer",
          "Color",
          "Cloud style",
          "Approximate arc length",
        ],
        properties: {
          Layer: "0",
          Color: "ByLayer",
          "Cloud style": command.revisionCloudStyle ?? "Normal",
          // Read-only until REVCLOUD-PROPS-EDITOR; Freehand persists as
          // Polygonal (see contracts/src/editor.ts).
          "Creation mode": rcMode === "Freehand" ? "Polygonal" : rcMode,
          "Approximate arc length": String(command.arcChordLength),
        },
      };

      return pushAuditEvent(
        {
          ...state,
          ui: { activePanel: "properties" },
          drawing: {
            ...state.drawing,
            entities: appendCreatedEntity(state.drawing.entities, rcEntity),
          },
          selection: { entityIds: [rcEntity.id] },
        },
        command,
        rcEntity.summary,
        "success",
        undefined,
        [rcEntity.id],
      );
    }

    case "geometry/create-hatch": {
      const hatchId = nextEntityId(state, "hatch", geometryDocument);
      const hatchEntity: EntityPreview = {
        id: hatchId,
        label: `Hatch ${hatchId}`,
        type: "Hatch",
        spaceId: command.spaceId,
        summary: `HATCH created with pattern ${command.patternName}.`,
        editableProperties: [
          "Layer",
          "Color",
          "Pattern",
          "Scale",
          "Associative",
        ],
        properties: {
          Layer: "0",
          Color: "ByLayer",
          Pattern: command.patternName,
          Scale: String(command.patternScale),
          Associative: "Yes",
        },
      };

      return pushAuditEvent(
        {
          ...state,
          ui: { activePanel: "properties" },
          drawing: {
            ...state.drawing,
            entities: appendCreatedEntity(state.drawing.entities, hatchEntity),
          },
          selection: { entityIds: [hatchEntity.id] },
        },
        command,
        hatchEntity.summary,
        "success",
        undefined,
        [hatchEntity.id],
      );
    }

    case "geometry/create-wipeout": {
      // WIPEOUT — opaque polygon that hides entities below. The shell
      // entity captures the vertex count + frame visibility so the
      // properties panel and audit trail reflect the new entity. The
      // geometry-document side (geometryReducer.ts) holds the actual
      // vertex coordinates for round-trip fidelity.
      const wipeoutId = nextEntityId(state, "wipeout", geometryDocument);
      const wipeoutEntity: EntityPreview = {
        id: wipeoutId,
        label: `Wipeout ${wipeoutId}`,
        type: "Wipeout",
        spaceId: command.spaceId,
        summary: `WIPEOUT created with ${command.vertices.length} vertices.`,
        editableProperties: ["Layer", "Color", "Frame"],
        properties: {
          Layer: "0",
          Color: "ByLayer",
          Frame: command.frame ? "On" : "Off",
        },
      };

      return pushAuditEvent(
        {
          ...state,
          ui: { activePanel: "properties" },
          drawing: {
            ...state.drawing,
            entities: appendCreatedEntity(
              state.drawing.entities,
              wipeoutEntity,
            ),
          },
          selection: { entityIds: [wipeoutEntity.id] },
        },
        command,
        wipeoutEntity.summary,
        "success",
        undefined,
        [wipeoutEntity.id],
      );
    }

    // ── BLOCK authoring (create-block-definition) ──
    // Shell-side is audit-only. The geometry document mutation (in
    // geometryReducer.ts) registers the new BlockDefinition + BlockRecord
    // and optionally appends a convert-INSERT; the editor shell is
    // re-synced from the updated geometry document after dispatch.
    case "geometry/create-block-definition": {
      return pushAuditEvent(
        state,
        command,
        t("audit.block_defined", { name: command.blockDef.name }),
        "success",
      );
    }

    // ── EXPLODE an INSERT entity (geometry/explode-insert) ──
    // Audit-only. The geometry reducer removes the INSERT and appends
    // its child entities; the shell is re-synced after.
    case "geometry/explode-insert": {
      const declined = explodeRefusalAudit(
        summarizeExplodeRefusalForIds(geometryDocument, [command.insertId]),
      );
      if (declined) return pushAuditEvent(state, command, declined, "warning");
      return pushAuditEvent(
        state,
        command,
        t("audit.insert_exploded"),
        "success",
      );
    }

    // ── PURGE unreferenced blocks (file/purge-blocks, Phase 2.7) ──
    // Audit-only on the shell side. The geometry reducer removes the
    // blocks from blocks[]/blockRecords[]; the shell is re-synced after.
    case "file/purge-blocks": {
      return pushAuditEvent(
        state,
        command,
        t("audit.blocks_purged", { count: command.blockNames.length }),
        "success",
      );
    }

    // ── RENAME a block with cascade (file/rename-block, Phase 2.8) ──
    // Audit-only on the shell side. The geometry reducer rewrites every
    // INSERT reference + blocks/blockRecords entries; the shell is
    // re-synced after.
    case "file/rename-block": {
      return pushAuditEvent(
        state,
        command,
        t("audit.block_renamed", {
          oldName: command.oldName,
          newName: command.newName,
        }),
        "success",
      );
    }

    // ── XREF suite (Phase FIN.3) ─────────────────────────────────
    // Audit-only on the shell side. Every xref command resyncs the
    // shell from the geometry document via the editorStore router.
    case "file/xattach": {
      return pushAuditEvent(
        state,
        command,
        t("audit.xref_attached", {
          name: command.xrefName,
          path: command.xrefPath,
        }),
        "success",
      );
    }
    case "file/xbind": {
      return pushAuditEvent(
        state,
        command,
        t("audit.xref_bound", { name: command.xrefName }),
        "success",
      );
    }
    case "file/xunload": {
      return pushAuditEvent(
        state,
        command,
        t("audit.xref_unloaded", { name: command.xrefName }),
        "success",
      );
    }
    case "file/xreload": {
      return pushAuditEvent(
        state,
        command,
        t("audit.xref_reloaded", { name: command.xrefName }),
        "success",
      );
    }
    case "file/xdetach": {
      return pushAuditEvent(
        state,
        command,
        t("audit.xref_detached", { name: command.xrefName }),
        "success",
      );
    }
    case "file/xclip": {
      return pushAuditEvent(
        state,
        command,
        command.boundary === null
          ? t("audit.xref_clip_cleared", { name: command.xrefName })
          : t("audit.xref_clipped", { name: command.xrefName }),
        "success",
      );
    }

    // ── ATTDEF authoring (geometry/create-attdef, Phase 2.1) ──
    // Audit-only on the shell side. The geometry reducer appends the
    // ATTDEF to blocks[name].entities; the shell is re-synced after.
    case "geometry/create-attdef": {
      return pushAuditEvent(
        state,
        command,
        t("audit.attdef_created", {
          tag: command.attdef.tag,
          block: command.blockName,
        }),
        "success",
      );
    }

    // ── ATTRIB update (geometry/update-attribute-value, Phase 2.2) ──
    case "geometry/update-attribute-value": {
      return pushAuditEvent(
        state,
        command,
        t("audit.attribute_updated", { tag: command.tag }),
        "success",
      );
    }

    // ── ATTRIB batch update (geometry/update-attribute-values, Phase 2.2) ──
    case "geometry/update-attribute-values": {
      return pushAuditEvent(
        state,
        command,
        t("audit.attributes_updated", { count: command.updates.length }),
        "success",
      );
    }

    // ── BATTMAN — ATTDEF removal (Phase 2.4) ──
    case "geometry/remove-attdef": {
      return pushAuditEvent(
        state,
        command,
        t("audit.attdef_removed", {
          id: command.attdefId,
          block: command.blockName,
        }),
        "success",
      );
    }

    // ── BATTMAN — ATTDEF update (Phase 2.4) ──
    case "geometry/update-attdef": {
      return pushAuditEvent(
        state,
        command,
        t("audit.attdef_updated", {
          id: command.attdefId,
          block: command.blockName,
        }),
        "success",
      );
    }

    // ── BATTMAN — ATTDEF tag rename (Phase 2.4) ──
    case "geometry/rename-attdef-tag": {
      return pushAuditEvent(
        state,
        command,
        t("audit.attdef_tag_renamed", {
          newTag: command.newTag,
          block: command.blockName,
        }),
        "success",
      );
    }

    // ── BATTMAN — ATTDEF reorder (Phase 2.4) ──
    case "geometry/reorder-attdefs": {
      return pushAuditEvent(
        state,
        command,
        t("audit.attdefs_reordered", {
          count: command.attdefIds.length,
          block: command.blockName,
        }),
        "success",
      );
    }

    // ── ATTSYNC (Phase 2.5) ──
    case "geometry/attsync": {
      return pushAuditEvent(
        state,
        command,
        t("audit.attsync", { block: command.blockName }),
        "success",
      );
    }

    // ── Dynamic block authoring audit events (DYN-1.7 + DYN-1.8) ──
    case "geometry/add-dynamic-parameter": {
      return pushAuditEvent(
        state,
        command,
        t("audit.dynamic_parameter_added", { block: command.blockName }),
        "success",
      );
    }
    case "geometry/update-dynamic-parameter": {
      return pushAuditEvent(
        state,
        command,
        t("audit.dynamic_parameter_updated", {
          id: command.parameterId,
          block: command.blockName,
        }),
        "success",
      );
    }
    case "geometry/remove-dynamic-parameter": {
      return pushAuditEvent(
        state,
        command,
        t("audit.dynamic_parameter_removed", {
          id: command.parameterId,
          block: command.blockName,
        }),
        "success",
      );
    }
    case "geometry/add-dynamic-action": {
      return pushAuditEvent(
        state,
        command,
        t("audit.dynamic_action_added", { block: command.blockName }),
        "success",
      );
    }
    case "geometry/update-dynamic-action": {
      return pushAuditEvent(
        state,
        command,
        t("audit.dynamic_action_updated", {
          id: command.actionId,
          block: command.blockName,
        }),
        "success",
      );
    }
    case "geometry/remove-dynamic-action": {
      return pushAuditEvent(
        state,
        command,
        t("audit.dynamic_action_removed", {
          id: command.actionId,
          block: command.blockName,
        }),
        "success",
      );
    }
    case "geometry/add-visibility-state": {
      return pushAuditEvent(
        state,
        command,
        t("audit.visibility_state_added", {
          name: command.stateName,
          block: command.blockName,
        }),
        "success",
      );
    }
    case "geometry/rename-visibility-state": {
      return pushAuditEvent(
        state,
        command,
        t("audit.visibility_state_renamed", {
          name: command.newName,
          block: command.blockName,
        }),
        "success",
      );
    }
    case "geometry/delete-visibility-state": {
      return pushAuditEvent(
        state,
        command,
        t("audit.visibility_state_deleted", {
          id: command.stateId,
          block: command.blockName,
        }),
        "success",
      );
    }
    case "geometry/reorder-visibility-states": {
      return pushAuditEvent(
        state,
        command,
        t("audit.visibility_states_reordered", { block: command.blockName }),
        "success",
      );
    }
    case "geometry/set-default-visibility-state": {
      return pushAuditEvent(
        state,
        command,
        t("audit.visibility_default_set", {
          id: command.stateId,
          block: command.blockName,
        }),
        "success",
      );
    }
    case "geometry/toggle-visibility-state-entity": {
      return pushAuditEvent(
        state,
        command,
        t("audit.visibility_entity_toggled", {
          entity: command.entityId,
          block: command.blockName,
        }),
        "success",
      );
    }
    // ── Phase K.4 — Visibility States Manager additions ───
    case "geometry/set-current-visibility-state": {
      return pushAuditEvent(
        state,
        command,
        t("audit.visibility_current_set", {
          id: command.stateId,
          block: command.blockName,
        }),
        "success",
      );
    }
    case "geometry/bvhide": {
      return pushAuditEvent(
        state,
        command,
        t("audit.bvhide", {
          count: command.entityIds.length,
          block: command.blockName,
        }),
        "success",
      );
    }
    case "geometry/bvshow": {
      return pushAuditEvent(
        state,
        command,
        t("audit.bvshow", {
          count: command.entityIds.length,
          block: command.blockName,
        }),
        "success",
      );
    }
    case "geometry/bvshow-all": {
      return pushAuditEvent(
        state,
        command,
        t("audit.bvshow_all", {
          count: command.entityIds.length,
          block: command.blockName,
        }),
        "success",
      );
    }
    // ── DYN-3 Batch C — Chain Actions ──
    case "geometry/create-chain-group":
    case "geometry/add-action-to-chain-group":
    case "geometry/remove-action-from-chain":
    case "geometry/reorder-chain-group": {
      return pushAuditEvent(
        state,
        command,
        t("audit.chain_group_updated", { block: command.blockName }),
        "success",
      );
    }
    // ── DYN-2 B.2 Lookup Tables + Block Properties Table ───
    case "geometry/create-lookup-table": {
      return pushAuditEvent(
        state,
        command,
        t("audit.lookup_table_created", { block: command.blockName }),
        "success",
      );
    }
    case "geometry/update-lookup-table": {
      return pushAuditEvent(
        state,
        command,
        t("audit.lookup_table_updated", {
          id: command.tableId,
          block: command.blockName,
        }),
        "success",
      );
    }
    case "geometry/delete-lookup-table": {
      return pushAuditEvent(
        state,
        command,
        t("audit.lookup_table_deleted", {
          id: command.tableId,
          block: command.blockName,
        }),
        "success",
      );
    }
    case "geometry/set-block-properties-table": {
      return pushAuditEvent(
        state,
        command,
        t("audit.block_properties_table_set", { block: command.blockName }),
        "success",
      );
    }
    case "geometry/add-block-property": {
      return pushAuditEvent(
        state,
        command,
        t("audit.block_property_added", { block: command.blockName }),
        "success",
      );
    }
    case "geometry/update-block-property": {
      return pushAuditEvent(
        state,
        command,
        t("audit.block_property_updated", {
          key: command.key,
          block: command.blockName,
        }),
        "success",
      );
    }
    case "geometry/remove-block-property": {
      return pushAuditEvent(
        state,
        command,
        t("audit.block_property_removed", {
          key: command.key,
          block: command.blockName,
        }),
        "success",
      );
    }
    case "geometry/reorder-block-properties": {
      return pushAuditEvent(
        state,
        command,
        t("audit.block_properties_reordered", { block: command.blockName }),
        "success",
      );
    }
    // ── Phase N.5 — Block Properties Manager (BPM) ──────────────
    case "geometry/rename-block-parameter": {
      return pushAuditEvent(
        state,
        command,
        t("audit.bpm_parameter_renamed", {
          id: command.parameterId,
          name: command.newName,
          block: command.blockName,
        }),
        "success",
      );
    }
    case "geometry/update-block-parameter-description": {
      return pushAuditEvent(
        state,
        command,
        t("audit.bpm_parameter_description_updated", {
          id: command.parameterId,
          block: command.blockName,
        }),
        "success",
      );
    }
    case "geometry/toggle-parameter-show-in-palette": {
      return pushAuditEvent(
        state,
        command,
        t("audit.bpm_parameter_show_toggled", {
          id: command.parameterId,
          block: command.blockName,
        }),
        "success",
      );
    }
    case "geometry/reorder-block-parameters": {
      return pushAuditEvent(
        state,
        command,
        t("audit.bpm_parameters_reordered", { block: command.blockName }),
        "success",
      );
    }
    case "geometry/rename-block-action": {
      return pushAuditEvent(
        state,
        command,
        t("audit.bpm_action_renamed", {
          id: command.actionId,
          name: command.newName,
          block: command.blockName,
        }),
        "success",
      );
    }
    case "geometry/update-block-action-description": {
      return pushAuditEvent(
        state,
        command,
        t("audit.bpm_action_description_updated", {
          id: command.actionId,
          block: command.blockName,
        }),
        "success",
      );
    }
    case "geometry/reorder-block-actions": {
      return pushAuditEvent(
        state,
        command,
        t("audit.bpm_actions_reordered", { block: command.blockName }),
        "success",
      );
    }

    case "geometry/create-block-insert": {
      const blockId = nextEntityId(state, "block", geometryDocument);
      const blockEntity: EntityPreview = {
        id: blockId,
        label: `${command.blockName} ${blockId}`,
        type: "Block",
        spaceId: command.spaceId,
        summary: `INSERT ${command.blockName} at ${command.insertionPoint.x},${command.insertionPoint.y}.`,
        editableProperties: [
          "Layer",
          "Color",
          "Name",
          "Insertion point",
          "Scale",
          "Rotation",
        ],
        properties: {
          Layer: "0",
          Color: "ByLayer",
          Name: command.blockName,
          "Insertion point": `${command.insertionPoint.x},${command.insertionPoint.y}`,
          Scale: `${command.scaleX},${command.scaleY},1`,
          Rotation: String(command.rotation),
        },
      };

      return pushAuditEvent(
        {
          ...state,
          ui: { activePanel: "properties" },
          drawing: {
            ...state.drawing,
            entities: appendCreatedEntity(state.drawing.entities, blockEntity),
          },
          selection: { entityIds: [blockEntity.id] },
        },
        command,
        blockEntity.summary,
        "success",
        undefined,
        [blockEntity.id],
      );
    }

    case "geometry/create-block-inserts-batch": {
      // T6b — AI-dispatch coalescing (an AI MINSERT run of ≥2 block-insert
      // copies). Same shell pattern as ARRAYRECT/ARRAYPOLAR below: the
      // geometry reducer appends every copy in ONE batch pass, so the shell
      // side only needs a summary/audit event — the unconditional resync
      // (driven by the returned `EntityMutation.added`) rebuilds all K entity
      // previews. Building K individual EntityPreview objects here (as the
      // single-insert case above does for K=1) would not scale to a
      // 50,000-copy MINSERT and would defeat the point of the batch.
      const summary = t("summary.block_insert_batch_created", {
        n: command.inserts.length,
        name: command.blockName,
      });

      return pushAuditEvent(
        { ...state, ui: { activePanel: "properties" } },
        command,
        summary,
        "success",
      );
    }

    case "geometry/create-arrayrect": {
      // Non-associative ARRAYRECT — individual entity copies are
      // produced by the geometry reducer. The shell side only needs
      // to emit a summary/audit event; the resync pass will pick up
      // the new copies via the standard entity flow.
      const numCopies =
        Math.max(0, command.rowCount * command.columnCount - 1) *
        command.entityIds.length;
      const summary = t("summary.arrayrect_created", { n: numCopies });

      return pushAuditEvent(
        {
          ...state,
          ui: { activePanel: "properties" },
        },
        command,
        summary,
        "success",
      );
    }

    case "geometry/create-arraypolar": {
      // Non-associative ARRAYPOLAR — individual entity copies are
      // produced by the geometry reducer (same flow as ARRAYRECT).
      // The shell side emits a summary/audit event; the resync pass
      // picks up the new copies via the standard entity flow.
      const numCopies =
        Math.max(0, command.itemCount * command.rowCount - 1) *
        command.entityIds.length;
      const summary = t("summary.arraypolar_created", { n: numCopies });

      return pushAuditEvent(
        {
          ...state,
          ui: { activePanel: "properties" },
        },
        command,
        summary,
        "success",
      );
    }

    case "geometry/create-arraypath": {
      // Non-associative ARRAYPATH — individual entity copies distributed
      // along a path curve are produced by the geometry reducer. The
      // shell side emits a summary/audit event; the resync pass picks
      // up the new copies via the standard entity flow.
      //
      // The exact copy count depends on the path length (for "measure")
      // which is not directly accessible here without duplicating the
      // sampling logic; for summary display we show a best-effort
      // estimate based on itemCount when available.
      //
      // The two NAMED refusals — `command-engine`'s subtype and plane
      // predicates — are pure, pre-mutation and answered here, in the document
      // reducer's own guard order, so the row names the reason it stops at.
      //
      // The success row below is OPTIMISTIC and is corrected in
      // `./editorStore.ts`'s dispatch updater once `dispatchMutations` exists:
      // `handleCreateArrayPath` also returns the document unchanged for six
      // reasons this shell cannot see (missing path id, non-curve type,
      // itemCount < 2, non-positive or over-long spacing, MAXARRAY, foreign
      // arrayGroupId), and reporting "Массив по пути создан (N копий)" for any
      // of them is a plain falsehood about the drawing, not merely an
      // imprecise estimate. Both halves of the wording live in
      // `./arrayPathShellAudit.ts`.
      const declinedPath = arrayPathDeclinedAudit(geometryDocument, command);
      if (declinedPath) {
        return pushAuditEvent(state, command, declinedPath, "warning");
      }
      const estimatedCopies =
        command.method === "divide" && command.itemCount
          ? Math.max(0, command.itemCount - 1) * command.entityIds.length
          : command.entityIds.length;
      const summary = t("summary.arraypath_created", { n: estimatedCopies });

      return pushAuditEvent(
        {
          ...state,
          ui: { activePanel: "properties" },
        },
        command,
        summary,
        "success",
      );
    }

    case "geometry/arrayedit": {
      // ARRAYEDIT — existing array copies are deleted and regenerated
      // with the new parameters by the geometry reducer. The shell
      // side emits a summary/audit event; the resync pass picks up
      // the updated entities via the standard entity flow.
      //
      // Estimate copy count from the new parameters (mode-specific).
      let estimatedCopies = 0;
      if (command.mode === "rect" && command.rect) {
        estimatedCopies = Math.max(
          0,
          command.rect.rowCount * command.rect.columnCount - 1,
        );
      } else if (command.mode === "polar" && command.polar) {
        estimatedCopies = Math.max(
          0,
          command.polar.itemCount * command.polar.rowCount - 1,
        );
      } else if (command.mode === "path" && command.path) {
        estimatedCopies =
          command.path.method === "divide" && command.path.itemCount
            ? Math.max(0, command.path.itemCount - 1)
            : 0;
      }
      const summary = t("summary.arrayedit_modified", { n: estimatedCopies });

      return pushAuditEvent(
        {
          ...state,
          ui: { activePanel: "properties" },
        },
        command,
        summary,
        "success",
      );
    }

    case "geometry/hatchedit": {
      // HATCHEDIT — the target HATCH's typed pattern fields are overwritten in
      // place by the geometry reducer; the resync pass picks up the updated
      // entity via the standard entity flow. Mirror arrayedit's audit-only shell
      // arm so a CLI / scene / AI HATCHEDIT emits its `pushAuditEvent` /
      // command-feed transcript row (Codex round-4 #2 — without this case the
      // dispatch fell through to the silent default: it mutated + recorded
      // history but left NO audit trail, unlike every sibling modify command).
      return pushAuditEvent(
        {
          ...state,
          ui: { activePanel: "properties" },
        },
        command,
        t("summary.hatchedit_modified"),
        "success",
      );
    }

    case "geometry/create-viewport": {
      // MVIEW is LAYOUT-ONLY: reject a non-paper-space target (spaceId =
      // "model" / orphan / block-edit) BEFORE the shell mutation, mirroring the
      // geometry reducer's `isPaperSpaceLayoutTarget` guard (single source of
      // truth). Without this mirror the geometry reducer rejects the document
      // mutation (NO_MUTATIONS) but the shell reducer would already have
      // appended a viewport preview + selected it, and the no-op resync
      // fast-path would preserve that shell-only phantom selected viewport with
      // no geometry-document entity / history entry (#287-11). Returning `state`
      // unchanged leaves no phantom and no selection change.
      if (!isPaperSpaceLayoutTarget(geometryDocument, command.spaceId)) {
        return state;
      }
      const vpId = nextEntityId(state, "viewport", geometryDocument);
      // Route through command-engine so viewCenterX/viewCenterY and
      // viewHeight are derived from the model extent when present
      // (AutoCAD MVIEW FIT parity).
      const { entity: vpEntity, summary: vpSummary } = createViewportEntity({
        nextId: vpId,
        spaceId: command.spaceId,
        corner1: command.corner1,
        corner2: command.corner2,
        ...(command.modelExtent ? { modelExtent: command.modelExtent } : {}),
      });

      return pushAuditEvent(
        {
          ...state,
          ui: { activePanel: "properties" },
          drawing: {
            ...state.drawing,
            entities: appendCreatedEntity(state.drawing.entities, vpEntity),
          },
          selection: { entityIds: [vpEntity.id] },
        },
        command,
        vpSummary,
        "success",
        undefined,
        [vpEntity.id],
      );
    }

    // ── Modification commands: audit events only ──────────────────
    // The actual geometry mutations happen in geometryReducer.ts.
    // The shell state is rebuilt via syncShellStateFromGeometryDocument
    // after the geometry document is updated.

    case "geometry/move-entities": {
      return pushAuditEvent(
        state,
        command,
        t("audit.entities_moved"),
        "success",
      );
    }

    case "geometry/copy-entities": {
      return pushAuditEvent(
        state,
        command,
        t("audit.entities_copied"),
        "success",
      );
    }

    case "geometry/copy": {
      // AutoCAD parity — COPY with explicit base + second point. Audit
      // message reports the number of clones so each iteration of the
      // multi-click loop leaves its own trail. Summary uses the pure
      // mutation's `clonedEntities.length`, derived here from the
      // sourceEntityIds payload (reducer downstream appends one clone
      // per source).
      return pushAuditEvent(
        state,
        command,
        t("audit.copy", { n: command.sourceEntityIds.length }),
        "success",
      );
    }

    case "geometry/mirror-entities": {
      // @codex PR #785 round 13, FINDING C — an `OLE Frame` has no rotation
      // group code, so only a map that keeps the frame axis-aligned can be
      // stored; a mirror about a SLANTED axis has no representable result and
      // `transformEntityCoords`'s `OLE Frame` arm returns the entity UNCHANGED
      // rather than committing the bounding box of the reflected corners. That
      // refusal must be VISIBLE, not silent (CLAUDE.md ПРИОРИТЕТ #1's
      // documented-incomplete-subsystem exception) — same warning-tone row
      // shape as the SCALE × `Table` refusal below.
      //
      // ONE validity truth with the transform arm: both reach
      // `command-engine/oleFrameTransformRefusal.ts` through the SAME affine
      // the document reducer applies (`mirrorPoint` with this command's own
      // axis, `geometryReducerEdit.ts`'s mirror arm), so the shell can never
      // report a refusal the document did not make, nor stay silent about one
      // it did. A horizontal / vertical axis and the two diagonals `y = x` /
      // `y = −x` are all exactly representable — the diagonals SWAP the frame's
      // two axes, which the stored corner pair names exactly — so they keep the
      // plain success row. (A degenerate zero-length axis never reaches here:
      // `editorCommandSchema` rejects the command outright.)
      // @codex PR #785 round 15, FINDING 2 — the TABLE twin of the refusal
      // directly above, and reported FIRST for the reason `explodeRefusalAudit`
      // already states for a definition that holds both kinds: a declined
      // TABLE wins over a declined OLE FRAME, so one dispatch raises one row
      // naming the kind whose refusal is the harder one to guess at. A table
      // is placed by an insertion point plus a horizontal-direction VECTOR and
      // carries no scale and no handedness field, so a REFLECTION comes back
      // as a 180° TURN — `transformEntityCoords`' `Table` arm therefore returns
      // the entity UNCHANGED, and that must be VISIBLE rather than silent
      // (CLAUDE.md ПРИОРИТЕТ #1's documented-incomplete-subsystem exception).
      //
      // ONE validity truth with the transform arm: both reach
      // `command-engine/tableMirrorRefusal.ts` through the SAME affine the
      // document reducer applies (`mirrorPoint` with this command's own axis),
      // so the shell can never report a refusal the document did not make, nor
      // stay silent about one it did. MIRROR is the only direct command that
      // can produce one: MOVE, ROTATE and SCALE all have `det(L) > 0`, pinned
      // by name in `editorStore.tableMirrorRefusal.test.ts`.
      const tableMirrorRefusal = summarizeTableMirrorRefusal(
        geometryDocument.spaces,
        command.entityIds,
        (p) => mirrorPoint(p, command.mirrorLine1, command.mirrorLine2),
      );
      if (tableMirrorRefusal.refusedEntityIds.length > 0) {
        return pushAuditEvent(
          state,
          command,
          t(
            tableMirrorRefusal.transformedCount > 0
              ? "audit.entities_mirrored_tables_declined"
              : "audit.mirror_declined_tables_only",
            { n: tableMirrorRefusal.refusedEntityIds.length },
          ),
          "warning",
        );
      }
      const oleMirrorRefusal = summarizeOleFrameTransformRefusal(
        geometryDocument.spaces,
        command.entityIds,
        (p) => mirrorPoint(p, command.mirrorLine1, command.mirrorLine2),
      );
      if (oleMirrorRefusal.refusedEntityIds.length > 0) {
        return pushAuditEvent(
          state,
          command,
          t(
            oleMirrorRefusal.transformedCount > 0
              ? "audit.entities_mirrored_ole_frames_declined"
              : "audit.mirror_declined_ole_frames_only",
            { n: oleMirrorRefusal.refusedEntityIds.length },
          ),
          "warning",
        );
      }
      return pushAuditEvent(
        state,
        command,
        t("audit.entities_mirrored"),
        "success",
      );
    }

    case "geometry/rotate-entities": {
      // @codex PR #785 round 13, FINDING C — the ROTATE twin of the MIRROR
      // refusal directly above. A turn by a multiple of 90° maps the frame's
      // corners onto the corners of another axis-aligned rectangle and is
      // applied exactly; any OTHER angle is refused by
      // `transformEntityCoords`'s `OLE Frame` arm and is reported here instead
      // of passing for a plain success. Same affine the document reducer
      // applies (`rotatePoint` about this command's own base point).
      const oleRotateRefusal = summarizeOleFrameTransformRefusal(
        geometryDocument.spaces,
        command.entityIds,
        (p) => rotatePoint(p, command.basePoint, command.angleDeg),
      );
      if (oleRotateRefusal.refusedEntityIds.length > 0) {
        return pushAuditEvent(
          state,
          command,
          t(
            oleRotateRefusal.transformedCount > 0
              ? "audit.entities_rotated_ole_frames_declined"
              : "audit.rotate_declined_ole_frames_only",
            { n: oleRotateRefusal.refusedEntityIds.length },
          ),
          "warning",
        );
      }
      return pushAuditEvent(
        state,
        command,
        t("audit.entities_rotated"),
        "success",
      );
    }

    case "geometry/scale-entities": {
      // GitHub Codex review PR #785 round 2, P2 — "Scale the table contents
      // along with its insertion point." SCALE cannot RESIZE a `Table`: its
      // size lives in the pre-evaluated `*T<N>` graphics block, for which
      // there is no generator, so the document reducer DECLINES a table at
      // `|factor| !== 1` (`geometryReducerEdit.ts`'s scale arm) instead of
      // sliding it to a scaled position at its original size. That refusal
      // must be VISIBLE, not silent — CLAUDE.md ПРИОРИТЕТ #1's one allowed
      // exception is a KNOWN incomplete subsystem that is DOCUMENTED, and a
      // warning-tone audit row is this repo's user-facing channel for "the
      // command declined part of your selection" (same shape as the REVCLOUD
      // arm's `prompt.revcloud.invalid_object` above).
      //
      // ONE validity truth with the document reducer: both call
      // `command-engine/tableScaleRefusal.ts` — the shell must never report a
      // refusal the document did not make, nor stay silent about one it did.
      // `|factor| === 1` (no-op / point reflection) refuses nothing and keeps
      // the plain success row, byte-for-byte as before this fix.
      const tableRefusal = summarizeTableScaleRefusal(
        geometryDocument.spaces,
        command.entityIds,
        command.factor,
      );
      if (tableRefusal.refusedEntityIds.length > 0) {
        return pushAuditEvent(
          state,
          command,
          t(
            tableRefusal.transformedCount > 0
              ? "audit.entities_scaled_tables_declined"
              : "audit.scale_declined_tables_only",
            { n: tableRefusal.refusedEntityIds.length },
          ),
          "warning",
        );
      }
      return pushAuditEvent(
        state,
        command,
        t("audit.entities_scaled"),
        "success",
      );
    }

    case "geometry/offset-entity": {
      // A heavy POLYLINE subtype OFFSET declines is reported, never silently
      // skipped (@codex PR #785 round 14, P2) — the document reducer's own arm
      // reads the same predicate and appends no clone for it.
      const declined = offsetHeavyPolylineRefusalAudit(
        heavyPolylineCurveRefusalFor(
          geometryDocument.spaces,
          command.entityId,
          "offset",
        ),
      );
      if (declined) return pushAuditEvent(state, command, declined, "warning");
      return pushAuditEvent(
        state,
        command,
        t("audit.entity_offset"),
        "success",
      );
    }

    case "geometry/stretch-entities": {
      return pushAuditEvent(
        state,
        command,
        t("audit.entities_stretched"),
        "success",
      );
    }

    // ── Batch 5: TRIM, EXTEND, FILLET, CHAMFER, BREAK, JOIN, EXPLODE, LENGTHEN

    case "geometry/trim-entity": {
      const refusal = summarizeHeavyPolylineTrimRefusal(
        geometryDocument.spaces,
        command.entityId,
        command.cuttingEdgeIds,
      );
      const declined = heavyPolylineRefusalAudit(refusal);
      if (declined) return pushAuditEvent(state, command, declined, "warning");
      return pushAuditEvent(
        state,
        command,
        t("audit.entity_trimmed"),
        "success",
      );
    }

    case "geometry/extend-entity": {
      const refusal = summarizeHeavyPolylineTrimRefusal(
        geometryDocument.spaces,
        command.entityId,
        command.boundaryEdgeIds,
      );
      const declined = heavyPolylineRefusalAudit(refusal);
      if (declined) return pushAuditEvent(state, command, declined, "warning");
      return pushAuditEvent(
        state,
        command,
        t("audit.entity_extended"),
        "success",
      );
    }

    case "geometry/fillet-entities": {
      return pushAuditEvent(
        state,
        command,
        t("audit.entities_filleted"),
        "success",
      );
    }

    case "geometry/chamfer-entities": {
      return pushAuditEvent(
        state,
        command,
        t("audit.entities_chamfered"),
        "success",
      );
    }

    case "geometry/break-entity": {
      return pushAuditEvent(
        state,
        command,
        t("audit.entity_broken"),
        "success",
      );
    }

    case "geometry/join-entities": {
      return pushAuditEvent(
        state,
        command,
        t("audit.entities_joined"),
        "success",
      );
    }

    case "geometry/explode-entities": {
      // The same refusal the single-INSERT arm above reports: EXPLODE of a
      // selection that includes such an INSERT leaves THAT insert alone (the
      // reducer's Block branch diffs before→after and sees no change), so the
      // warning row is what tells the user why one object stayed whole.
      const declined = explodeRefusalAudit(
        summarizeExplodeRefusalForIds(geometryDocument, command.entityIds),
      );
      if (declined) return pushAuditEvent(state, command, declined, "warning");
      // @codex PR #785 round 14, FINDING 2 — the same rule for a selected
      // TOP-LEVEL heavy POLYLINE the reducer's `"Polyline"` arm declined.
      const declinedPolyline = heavyPolylineExplodeRefusalAudit(
        summarizeHeavyPolylineExplodeRefusalForIds(
          geometryDocument,
          command.entityIds,
        ),
      );
      if (declinedPolyline) {
        return pushAuditEvent(state, command, declinedPolyline, "warning");
      }
      return pushAuditEvent(
        state,
        command,
        t("audit.entities_exploded"),
        "success",
      );
    }

    case "geometry/lengthen-entity": {
      return pushAuditEvent(
        state,
        command,
        t("audit.entity_lengthened"),
        "success",
      );
    }

    // ── DYNLIST / DYNSET / DYNRESET (INSERT customProperties CLI) ──
    // Audit events only — the rich transcript lives in App.tsx's
    // command feed because formatting needs geometryDocument.blocks
    // for typed parameter metadata. The shell side tracks a short
    // one-line audit summary so the history panel reflects the action.
    case "dynamic/list": {
      return pushAuditEvent(
        state,
        command,
        t("audit.dynlist", { id: command.entityId }),
        "info",
      );
    }
    case "dynamic/set": {
      return pushAuditEvent(
        state,
        command,
        t("audit.dynset", {
          id: command.entityId,
          param: command.paramName,
          value: command.rawValue,
        }),
        "success",
      );
    }
    case "dynamic/reset": {
      return pushAuditEvent(
        state,
        command,
        t("audit.dynreset", { id: command.entityId }),
        "success",
      );
    }
    // Phase N.4 — per-INSERT representation override audit events.
    // Re-use the existing dynset/dynreset audit message keys; the
    // override commands are functionally a typed superset that also
    // marks the divergence on representationOverrides for DXF
    // round-trip fidelity.
    case "geometry/set-insert-parameter-override": {
      return pushAuditEvent(
        state,
        command,
        t("audit.dynset", {
          id: command.entityId,
          param: command.paramName,
          value: command.rawValue,
        }),
        "success",
      );
    }
    case "geometry/clear-insert-parameter-override": {
      return pushAuditEvent(
        state,
        command,
        t("audit.dynreset", { id: command.entityId }),
        "success",
      );
    }
    case "geometry/set-insert-visibility-state": {
      return pushAuditEvent(
        state,
        command,
        t("audit.dynset", {
          id: command.entityId,
          param: "VisibilityState",
          value: command.stateId,
        }),
        "success",
      );
    }

    // ── CC-1c — per-viewport edit shell audit (Phase MC5 VPLAYER / VPCLIP /
    // lock / scale / view). These six commands mutate the geometry document
    // via `applyLayoutAction` but previously hit this `default → null` and
    // emitted NO audit event. The emitter lives in `geometryShellViewportAudit`
    // (kept out-of-line to hold this file under the 2000-line limit); it
    // returns the audited state for those six types, else null so non-viewport
    // commands fall through unchanged.
    default:
      return reduceViewportEditShellAudit(state, command, geometryDocument);
  }
}
